import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from 'src/api/client';
import { SortField } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';

export type IProfileNoteAttachment = {
  referenceName?: string;
  name: string;
  extension?: string;
  url: string;
};

export type IProfileNoteUser = {
  id: string;
  firstName?: string;
  lastName?: string;
};

export type IProfileNote = {
  id: string;
  userId: string;
  organizationId?: string;
  submissionContextId?: string;
  workspaceId?: string;
  title: string;
  content: string;
  journal?: {
    id: string;
    name: string;
    shortname: string;
  } | null;
  attachments?: IProfileNoteAttachment[];
  createdBy?: string;
  updatedBy?: string;
  isSystemGenerated?: boolean;
  created_at?: string;
  modified_at?: string;
  createdByUser?: IProfileNoteUser;
  updatedByUser?: IProfileNoteUser;
};

export type IProfileNotesResponse = {
  total: number;
  items: IProfileNote[];
};

export type IProfileNotePayload = {
  title?: string;
  content?: string;
  submissionContextId?: string;
  attachments?: IProfileNoteAttachment[];
};

export type IProfileNoteFilters = {
  searchParams?: string;
  journalId?: string[];
  createdBy?: string[];
  updatedBy?: string[];
};

export type IProfileNoteFilterUserOption = {
  id: string;
  firstName?: string;
  lastName?: string;
};

export type IProfileNoteFilterJournalOption = {
  id: string;
  name: string;
  shortname: string;
};

export type IProfileNoteFilterOptions = {
  journals?: IProfileNoteFilterJournalOption[];
  createdBy: IProfileNoteFilterUserOption[];
  updatedBy: IProfileNoteFilterUserOption[];
};

export function fetchProfileNotesFilters({ userId }: { userId: string }) {
  return axiosInstance
    .get<IProfileNoteFilterOptions>(`/users/${userId}/profile-notes/filters`)
    .then(r => r.data);
}

const getErrorMessage = (error: unknown, fallback: string) => {
  const axiosError = error as {
    response?: { data?: { message?: string } | string };
    message?: string;
  };

  return axiosError?.response?.data &&
    typeof axiosError.response.data === 'object' &&
    'message' in axiosError.response.data
    ? axiosError.response.data.message
    : typeof axiosError?.response?.data === 'string'
    ? axiosError.response.data
    : axiosError?.message || fallback;
};

const toResult = async <T>(promise: Promise<{ data: T }>, fallback: string) => {
  try {
    const response = await promise;
    return {
      isSuccess: true as const,
      message: fallback,
      data: response.data
    };
  } catch (error) {
    return {
      isSuccess: false as const,
      message: getErrorMessage(error, fallback)
    };
  }
};

export function fetchProfileNotes({
  userId,
  offset = 0,
  limit = 10,
  filters,
  sort
}: {
  userId: string;
  offset?: number;
  limit?: number;
  filters?: IProfileNoteFilters;
  sort?: SortField | null;
}) {
  return axiosInstance
    .get<IProfileNotesResponse>(`/users/${userId}/profile-notes`, {
      params: {
        offset,
        limit,
        filters: JSON.stringify(filters ?? {}),
        ...(sort && { sort: JSON.stringify(sort) })
      }
    })
    .then(r => r.data);
}

export const useCreateProfileNote = ({
  userId,
  queryKey
}: {
  userId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IProfileNotePayload) =>
      toResult(
        axiosInstance.post(`/users/${userId}/profile-notes`, payload),
        'Failed to create note'
      ),
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({ queryKey: ['profile-notes-filters'] });
      }
    }
  });
};

export const useUpdateProfileNote = ({
  userId,
  queryKey
}: {
  userId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      noteId,
      payload
    }: {
      noteId: string;
      payload: IProfileNotePayload;
    }) =>
      toResult(
        axiosInstance.put(`/users/${userId}/profile-notes/${noteId}`, payload),
        'Failed to update note'
      ),
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({ queryKey: ['profile-notes-filters'] });
      }
    }
  });
};

export const useDeleteProfileNotes = ({
  userId,
  queryKey
}: {
  userId: string;
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteIds: string[]) =>
      toResult(
        axiosInstance.delete(`/users/${userId}/profile-notes`, {
          data: { noteIds }
        }),
        'Failed to delete note'
      ),
    onSuccess: data => {
      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({ queryKey: ['profile-notes-filters'] });
      }
    }
  });
};
